import { differenceInDays } from 'date-fns';

export interface EcommerceDataItem {
    item_id: string;
    item_name?: string;
    affiliation: string;
    item_category: string;
    item_category4?: string;
    price?: number;
    quantity?: number;
    keycard?: string;
    first_day_of_validity?: string;
    validity_period?: string;
    n_days_in_advance?: string;
    destination_start?: string;
    destination_end?: string;
    discount_card?: string;
    coupon?: string;
}

export interface NonEcommerceData {
    option_type?: ProductKey;
}

export interface FilterEventDataAccommodation {
    filter_element_item: 'accommodation';
    filter_element_action?: 'select' | 'reject' | 'reset';
    filter_category_location?: string;
    filter_category?: string;
    filter_category_extras?: string;
    filter_result_sorting?: 'hotelname' | 'price_asc' | 'price_desc' | 'rating_asc' | 'rating_desc';
}

export interface FilterEventDataP2P {
    filter_sort: 'abfahrt' | 'ankunft';
    filter_time: string;
}

export type FilterEventData = FilterEventDataAccommodation | FilterEventDataP2P;

export interface EcommerceData {
    currency?: string;
    value?: number;
    items: EcommerceDataItem[];
    transaction_id?: string;
    coupon?: string;
    item_list_id?: string;
    item_list_name?: string;
    cart_type?: 'single' | 'mixed';
}

export type EcommerceEventName = 'add_guest' | 'add_to_cart' | 'remove_from_cart' | 'view_cart' | 'begin_checkout' | 'add_shipping_info' | 'add_payment_info' | 'purchase' | 'add_option' | 'view_item_list' | 'view_item' | 'select_item';

export interface UXInteractionData {
    element_name?: string;
    element_action?: string;
    destination_start_selected?: string;
    destination_end_selected?: string;
    route_selection?: string;
}

/**
 * Extract "traffic referrer" from path and store it in sessionStorage
 */
function handleTrafficReferrer(path: string): string | undefined {
    const SESSION_STORAGE_KEY = 'trafficReferrer';

    try {
        const existingsReferrers = sessionStorage.getItem(SESSION_STORAGE_KEY)?.split(',') || [];
        const [, newReferrer] = path.match(/\/(?:booking|addon)\/(.*?)\//) || [];
        const combinedReferrers = [...existingsReferrers, newReferrer].filter(Boolean);
        const referrer = [...new Set(combinedReferrers)].join(',');

        sessionStorage.setItem(SESSION_STORAGE_KEY, referrer);

        return referrer;
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);

        return undefined;
    }
}

/**
 * Extract listing ID from product config, fall back to activity ID if present
 */
function getListingId(item?: Product | BookedOffer): string | undefined {
    if (!item) {
        return undefined;
    }

    if ('activityId' in item && item.activityId) {
        return item.activityId;
    }

    if (('config' in item)) {
        if ('listingId' in item.config && item.config.listingId) {
            return item.config.listingId;
        }

        if ('activityId' in item.config && item.config.activityId) {
            return item.config.activityId;
        }
    }

    return undefined;
}

/**
 * Push data to dataLayer
 */
export function analyticsTrackEvent(event: any, debugEventName: string) {
    useNuxtApp().$gtmReady?.then((enabled) => {
        if (!enabled) {
            return;
        }

        // @ts-expect-error Conflict with `GoogleTagManagerApi` types
        window.dataLayer ||= [];
        window.dataLayer.push(event);
        logDebugAnalytics(debugEventName, event);
    }).catch((error) => {
        handleError(new Error('Error sending analytics event', { cause: error }), false);
    });
}

/**
 * Track Page View for Analytics
 */
export function analyticsTrackPageView(title: string | undefined, path: string, isPageInitialLoad: boolean = true) {
    useNuxtApp().$gtmReady?.then((enabled) => {
        if (!enabled) {
            return;
        }

        const locale = useNuxtApp().$i18n.locale.value;
        const { options: { history }, currentRoute } = useRouter();
        const url = useRequestURL();
        const runtimeConfig = useRuntimeConfig();
        const { product } = useProduct();

        const referrer = history.state.back
            ? `${url.origin}${history.state.back}`
            : document.referrer ?? undefined;

        const language = locale
            .replace('de', 'de_ch')
            .replace('fr', 'fr_fr')
            .replace('en', 'en_gb');

        const bookingId = (currentRoute.value.params as Record<string, any>).bookingId;
        const id = url.pathname
            // Remove locale
            .replace(new RegExp(`^/${locale}`), '')
            // remove :bookingId from checkout and esav pages
            .replace(bookingId ? new RegExp(`/${bookingId}`) : '', '');

        let pageCategory;
        let pageItemCategory;

        try {
            if (product?.value) {
                pageCategory = product.value.isUpsell ? 'upsell' : 'main_ticket';
                pageItemCategory = product.value.config.type;

                if ('hasEPR' in product.value.config && product.value.config.hasEPR) {
                    if (product.value.config.hasEPR === 'only') {
                        pageItemCategory = 'Reservation';
                    }
                    else {
                        pageItemCategory += ' + Reservation';
                    }
                }

                if (product.value.content?.stepActivityOptions) {
                    pageItemCategory += ' + Time Selection';
                }
            }
        }
        catch (e) {
        }

        analyticsTrackEvent({
            event: 'page_view',
            platform_environment: runtimeConfig.public.gtm.env,
            platform_name: 'top-of-travel',
            platform_interface_version: 'europe',
            page_url: url.href,
            page_url_path: url.pathname,
            page_url_hostname: url.origin,
            page_url_query_string: url.search,
            page_id: id,
            page_title: title,
            page_language: language,
            page_referrer: referrer,
            page_referrer_traffic: handleTrafficReferrer(url.pathname),
            page_initial_load: isPageInitialLoad,
            ...(pageCategory
                ? {

                        page_category: pageCategory,
                        page_item_category: pageItemCategory,
                        page_item_category4: getListingId(product.value),
                    }
                : undefined),
        }, 'analyticsTrackPageView');
    }).catch((error) => {
        handleError(error, false);
    });
}

/**
 * Track UX Interaction for Analytics
 */
export function analyticsTrackUXInteraction(elementType: string = '', elementText?: string, elementContext?: string, eventData?: UXInteractionData) {
    try {
        analyticsTrackEvent({
            event: 'ux_interaction',
            element_type: elementType,
            ...(elementText ? { element_text: elementText } : undefined),
            ...(elementContext ? { element_context: elementContext } : undefined),
            ...eventData,
        }, 'analyticsTrackUXInteraction');
    }
    catch (error) {
        handleError(error, false);
    }
}

/**
 * Track Errors for Analytics
 */
export function analyticsTrackError(errorCode: string = '', errorElementName: string = '', errorMessage: string = '') {
    try {
        analyticsTrackEvent({
            event: 'error_custom',
            error_code: errorCode,
            error_element_name: errorElementName,
            error_message: errorMessage,
        }, 'analyticsTrackError');
    }
    catch (error) {
        handleError(error, false);
    }
}

/**
 * Track eCommerce Event for Analytics
 */
export function analyticsTrackEcommerce(eventName: EcommerceEventName, ecommerceData?: EcommerceData, nonEcommerceData?: NonEcommerceData) {
    try {
        // Reset existing data
        analyticsTrackEvent({
            ecommerce: null,
        }, 'analyticsTrackEcommerceReset');

        // Submit new data
        analyticsTrackEvent({
            event: eventName,
            ...nonEcommerceData,
            ...(ecommerceData ? { ecommerce: ecommerceData } : null),
        }, 'analyticsTrackEcommerce');
    }
    catch (error) {
        handleError(error, false);
    }
}

/**
 * Temporarily create SKU from item name
 */
export function analyticsGetEcommerceItemId(itemName: string) {
    const itemId = itemName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_{2,}/g, '_');

    return itemId;
}

/**
 * Extract eCommerce tracking data from offer part
 */
export function analyticsGetEcommerceItem({ offerPart, parent, passengers }: { offerPart: OfferPart | BookedOfferPart; parent?: Product | BookedOffer; passengers?: Passenger[] }): EcommerceDataItem {
    const passenger = 'passengerRefs' in offerPart ? passengers?.find(p => offerPart.passengerRefs.includes(p.ref)) : undefined;
    const reductionCard = passenger?.reductionCards.map(card => card.name).join(', ');
    const item: EcommerceDataItem = {
        item_id: analyticsGetEcommerceItemId(offerPart.summary),
        item_name: offerPart.summary,
        affiliation: 'Top of Travel Online Shop',
        item_category: '',
        item_category4: getListingId(parent),
        price: analyticsGetOfferPartPrice(offerPart),
        quantity: 1,
        coupon: 'promotionCodes' in offerPart ? offerPart.promotionCodes?.map(p => p.name).join(',') : undefined,
        discount_card: reductionCard,
    };

    switch (offerPart.type) {
        case 'Ancillary':
        case 'AncillaryOfferPart': {
            item.item_category = 'Ancillary';

            break;
        }
        case 'Activity':
        case 'ActivityOfferPart': {
            const activity = parent as ProductActivity | BookedOfferActivity | undefined;
            const days = activity?.dateFrom && activity?.dateUntil ? differenceInDays(activity?.dateUntil, activity?.dateFrom) : 0;

            item.item_category = 'Activity';
            item.first_day_of_validity = formatDate(activity?.dateFrom, 'yyyy-MM-dd');
            item.validity_period = String(days);
            if (activity?.dateFrom) {
                item.n_days_in_advance = String(differenceInDays(activity?.dateFrom, zurichDate()));
            }

            break;
        }
        case 'Accommodation':
        case 'AccommodationOfferPart': {
            item.item_category = 'Accommodation';

            break;
        }
        case 'Reservation':
        case 'ReservationOfferPart':
        case 'Admission':
        case 'AdmissionOfferPart': {
            const isReservation = ['Reservation', 'ReservationOfferPart'].includes(offerPart.type);
            const p2p = parent as ProductP2P | BookedOfferP2P | undefined;
            const trips = [p2p?.tripUp, p2p?.tripDown].filter((Boolean)) as Trip[];
            const trip = trips?.find(t => offerPart.tripCoverage.tripIds.includes(t.id));

            item.item_category = isReservation ? 'Reservation' : 'P2P';
            item.destination_start = trip?.origin?.name;
            item.destination_end = trip?.destination?.name;
            item.first_day_of_validity = formatDate(p2p?.dateFrom, 'yyyy-MM-dd');
            if (p2p?.dateFrom) {
                item.n_days_in_advance = String(differenceInDays(p2p.dateFrom, zurichDate()));
            }

            break;
        }
    }

    return item;
}

/**
 * Sum up price of offer part and its optional reservation sub-parts
 */
export function analyticsGetOfferPartPrice(offerPart: OfferPart | BookedOfferPart): number | undefined {
    let price = offerPart.price ? offerPart.price.amount : undefined;

    if ((offerPart as OfferPartAdmission).reservationOfferParts) {
        for (const reservationOfferPart of (offerPart as OfferPartAdmission).reservationOfferParts!) {
            price = price || 0;
            price += reservationOfferPart.price.amount;
        }
    }

    return price ? price / 100 : undefined;
}

/**
 * Track filter event
 */
export function analyticsTrackFilter(eventData: FilterEventData) {
    try {
        analyticsTrackEvent({
            event: 'filter',
            ...eventData,
        }, 'analyticsTrackFilter');
    }
    catch (error) {
        handleError(error, false);
    }
}

/**
 * Some watchers track intermediate events we want to ignore
 * So we pick the old value of the first event and the new value of the last event
 */
export function analyticsDebouncedWatcherWithQueue<T>(fn: (newVal: T | undefined, oldVal: T | undefined) => void, wait: number | (() => boolean) = 100) {
    let queue: Array<{ newVal: T | undefined; oldVal: T | undefined }> = [];

    const flush = () => {
        try {
            const newVal = queue[queue.length - 1]?.newVal;
            const oldVal = queue[0]?.oldVal;

            fn(newVal, oldVal);

            queue = [];
        }
        catch (error) {
            handleError(error, false);
        }
    };

    const debouncedFlush = typeof wait === 'function'
        ? () => {
                if (wait()) {
                    flush();
                }
            }
        : useDebounceFn(() => {
                flush();
            }, wait);

    return (newVal: T | undefined, oldVal: T | undefined) => {
        queue.push({ newVal, oldVal });

        debouncedFlush();
    };
}
